/**
 * Shared font-usage resolution + base64 embedding helpers, used to turn a
 * (family, weight, style) usage into an actual embeddable font — either a
 * Google Fonts TTF (resolved via the plugin's `google_font_ttf` proxy) or a
 * custom uploaded font from the site's font catalog.
 *
 * Consumers: `design-download/usePdfVectorExport.ts` (embeds into a jsPDF
 * document) and `designer-app/composables/useFontEmbedding.ts` (embeds as a
 * `<style>@font-face{...}</style>` block into generated SVG markup).
 */

export interface FontCatalogItem {
  id?: string | number;
  title?: string;
  subtitle?: string;
  type?: string;
  filename?: string;
  url?: string;
  path?: string;
  published?: number | string;
  font_preview_url?: string;
}

export type FontWeight = 'normal' | 'bold';
export type FontStyle = 'normal' | 'italic';

export interface FontUsage {
  family: string;
  weight: FontWeight;
  style: FontStyle;
}

export const GENERIC_FAMILIES = new Set([
  'arial',
  'helvetica',
  'sans-serif',
  'serif',
  'monospace',
  'cursive',
  'fantasy',
  'system-ui',
]);

export function normalizeFamily(family: string): string {
  return family.trim().replace(/^['"]|['"]$/g, '').replace(/\s+/g, ' ');
}

export function normalizeWeight(raw: string | null | undefined): FontWeight {
  const value = (raw || '').trim().toLowerCase();
  if (value === 'bold') return 'bold';
  const numeric = Number.parseInt(value, 10);
  return Number.isFinite(numeric) && numeric >= 600 ? 'bold' : 'normal';
}

export function normalizeStyle(raw: string | null | undefined): FontStyle {
  const value = (raw || '').trim().toLowerCase();
  return value === 'italic' || value === 'oblique' ? 'italic' : 'normal';
}

/** One entry per distinct (family, weight, style) actually used by a `<text>` node, so bold/italic runs get their own embedded variant instead of silently reusing the regular one. */
export function collectTextFontUsages(root: SVGSVGElement): FontUsage[] {
  const out = new Map<string, FontUsage>();
  root.querySelectorAll('text').forEach((node) => {
    const raw = node.getAttribute('font-family') || '';
    const weight = normalizeWeight(node.getAttribute('font-weight'));
    const style = normalizeStyle(node.getAttribute('font-style'));
    raw.split(',').map(normalizeFamily).filter(Boolean).forEach((family) => {
      if (GENERIC_FAMILIES.has(family.toLowerCase())) return;
      out.set(`${family.toLowerCase()}|${weight}|${style}`, { family, weight, style });
    });
  });
  return [...out.values()];
}

function parseFontFiles(font: FontCatalogItem): string[] {
  const raw = font.filename;
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) return parsed.map(String);
    if (parsed && typeof parsed === 'object') return Object.values(parsed as Record<string, unknown>).map(String);
  } catch {
    return [raw];
  }
  return [];
}

function bestFontFile(font: FontCatalogItem): string {
  const files = parseFontFiles(font)
    .map((entry) => entry.replace(/\\/g, '/').split('/').pop() || '')
    .filter(Boolean);
  return files.find((file) => /\.(ttf|otf)$/i.test(file)) || files.find((file) => /\.(woff2?|ttf|otf)$/i.test(file)) || '';
}

export function buildCustomFontUrl(font: FontCatalogItem, pluginBaseUrl: string): string {
  const file = bestFontFile(font);
  if (!file) return '';
  if (/^https?:\/\//i.test(file)) return file;
  if (font.font_preview_url) return font.font_preview_url;
  const fontBase =
    font.url ||
    (pluginBaseUrl
      ? pluginBaseUrl + String(font.path || '').replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/?$/, '/')
      : '');
  if (!fontBase) return '';
  return new URL(file, fontBase).toString();
}

export function findCatalogFont(family: string, fonts: FontCatalogItem[]): FontCatalogItem | null {
  const wanted = family.trim().toLowerCase();
  return fonts.find((font) => String(font.title || '').trim().toLowerCase() === wanted) || null;
}

export function isGoogleFontCatalogEntry(font: FontCatalogItem | null): boolean {
  if (!font) return false;
  const type = String(font.type || '').trim().toLowerCase();
  return type === 'google' || type === '' || (!font.filename && !!font.subtitle);
}

export function googleFontSpecsIncludeFamily(specs: string | undefined, family: string): boolean {
  const wanted = family.trim().toLowerCase().replace(/\s+/g, '+');
  return String(specs || '')
    .split('|')
    .some((spec) => spec.split(':')[0].trim().toLowerCase() === wanted);
}

function arrayBufferToBinaryString(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let out = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    out += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return out;
}

export async function fetchBinaryAsBase64(url: string): Promise<string> {
  const response = await fetch(url, { credentials: 'same-origin' });
  if (!response.ok) throw new Error(`Font fetch failed: ${response.status}`);
  return btoa(arrayBufferToBinaryString(await response.arrayBuffer()));
}

/** Factory for a per-consumer base64 cache, keyed by font URL. */
export function createFontBase64Cache(): (url: string) => Promise<string> {
  const cache = new Map<string, Promise<string>>();
  return (url: string): Promise<string> => {
    let entry = cache.get(url);
    if (!entry) {
      entry = fetchBinaryAsBase64(url);
      cache.set(url, entry);
    }
    return entry;
  };
}

function appendQueryParam(url: string, key: string, value: string): string {
  const parsed = new URL(url, window.location.href);
  parsed.searchParams.set(key, value);
  return parsed.toString();
}

interface GoogleFontTtfResponse {
  error?: number | string;
  msg?: string;
  url?: string;
}

export async function resolveGoogleTtfUrl(
  endpoint: string,
  family: string,
  weight: FontWeight,
  style: FontStyle,
): Promise<string> {
  let url = appendQueryParam(endpoint, 'family', family);
  url = appendQueryParam(url, 'weight', weight === 'bold' ? '700' : '400');
  url = appendQueryParam(url, 'italic', style === 'italic' ? '1' : '0');
  const response = await fetch(url, { credentials: 'same-origin' });
  const data = await response.json().catch(() => ({})) as GoogleFontTtfResponse;
  if (!response.ok || Number(data.error || 0) !== 0 || !data.url) {
    throw new Error(data.msg || 'Google font TTF lookup failed');
  }
  return data.url;
}

export function fontExtFromUrl(url: string): string {
  try {
    const ext = new URL(url, window.location.href).pathname.match(/\.(otf|ttf|woff2?|ttc)$/i)?.[0];
    return ext || '.ttf';
  } catch {
    const match = url.match(/\.(otf|ttf|woff2?|ttc)(?:[?#]|$)/i);
    return match?.[1] ? `.${match[1]}` : '.ttf';
  }
}

/** Maps a font file extension to its CSS `format()` keyword and data-URI MIME type. */
export function fontFormatFromExt(ext: string): { format: string; mime: string } {
  const e = ext.replace(/^\./, '').toLowerCase();
  if (e === 'woff2') return { format: 'woff2', mime: 'font/woff2' };
  if (e === 'woff') return { format: 'woff', mime: 'font/woff' };
  if (e === 'otf') return { format: 'opentype', mime: 'font/otf' };
  return { format: 'truetype', mime: 'font/ttf' };
}

export function makeFontFileName(family: string, weight: FontWeight, style: FontStyle, ext: string): string {
  const suffix = `${weight === 'bold' ? '-bold' : ''}${style === 'italic' ? '-italic' : ''}`;
  return family.trim().replace(/[^A-Za-z0-9_-]+/g, '-') + suffix + ext;
}

export interface ResolveFontUrlOptions {
  catalog: FontCatalogItem[];
  googleFontSpecs?: string;
  googleFontTtfUrl: string;
  pluginBaseUrl: string;
}

/** Resolve a font usage to a fetchable font file URL (Google TTF or custom upload), or '' if unresolvable. */
export async function resolveFontUsageUrl(
  usage: FontUsage,
  opts: ResolveFontUrlOptions,
): Promise<string> {
  const { family, weight, style } = usage;
  const catalogFont = findCatalogFont(family, opts.catalog);
  if (isGoogleFontCatalogEntry(catalogFont) || (!catalogFont && googleFontSpecsIncludeFamily(opts.googleFontSpecs, family))) {
    if (!opts.googleFontTtfUrl) throw new Error('Google font endpoint missing');
    return resolveGoogleTtfUrl(opts.googleFontTtfUrl, family, weight, style);
  }
  if (catalogFont) {
    return buildCustomFontUrl(catalogFont, opts.pluginBaseUrl);
  }
  return '';
}

export interface ResolveFontFaceCssOptions extends ResolveFontUrlOptions {
  getFontBase64: (url: string) => Promise<string>;
}

export interface ResolveFontFaceCssResult {
  css: string;
  substituted: boolean;
  fontUrl?: string;
}

/** Resolve a font usage to a full `@font-face{...}` CSS string with a base64-embedded `src`. Never throws — returns `substituted: true` on any failure. */
export async function resolveFontFaceCss(
  usage: FontUsage,
  opts: ResolveFontFaceCssOptions,
): Promise<ResolveFontFaceCssResult> {
  const { family, weight, style } = usage;
  try {
    const fontUrl = await resolveFontUsageUrl(usage, opts);
    if (!fontUrl) return { css: '', substituted: true };
    const base64 = await opts.getFontBase64(fontUrl);
    const { format, mime } = fontFormatFromExt(fontExtFromUrl(fontUrl));
    const css = `@font-face{font-family:'${family.replace(/'/g, "\\'")}';font-style:${style};font-weight:${weight === 'bold' ? 700 : 400};src:url(data:${mime};base64,${base64}) format('${format}');}`;
    return { css, substituted: false, fontUrl };
  } catch (error) {
    console.warn('[fontEmbedding] font embed skipped', family, weight, style, error);
    return { css: '', substituted: true };
  }
}
